import { Injectable } from "@angular/core";
import { BaseService } from "../../../../core/services/base.service";
import { Observable, of } from "rxjs";
import { AppDataService } from "../../../../core/services/app-data.service";
import { AppBrandingSettingView } from "./models/app-branding-setting-view";

@Injectable({ providedIn: 'root' })
export class BrandingService extends BaseService {
    selectedBaseColor: string = '';
    baseColors: any[] = [];
    systemNameAr: string = '';
    systemNameEn: string = '';
    copyRightAr: string = '';
    copyRightEn: string = '';

    selectedColorId: any;
    defaultThemeData: any;
    logo!: string | ArrayBuffer | null;
    darkLogo!: string | ArrayBuffer | null;
    backgroundImage!: string | ArrayBuffer | null;
    loader!: string | ArrayBuffer | null;
    selectedFont: any;
    isBusy: boolean = false;
    showThemeDialog: boolean = false;
    systemFontValue: string = '';

    rejectColors: any[] = [];
    selectedRejectColorId: any;
    selectedRejectColor: any;

    approveColors: any[] = [];
    selectedApproveColorId: any;
    selectedApproveColor: any;

    constructor(private configService: AppDataService) {
        super('/V1/AppBranding');
    }
   

    getBrandingSettings(): Observable<AppBrandingSettingView | null> {
        return super.request<AppBrandingSettingView>('Get', {}, 'get');
    }
    isBase64(str: string) {
        try {
            return btoa(atob(str)) === str;
        } catch (err) {
            return false;
        }
    }
    getAllFonts(): Observable<any[]> {
        // return of(this.fonts);
        return super.request('GetFonts', {}, 'get');
    }
    getBaseColors(): Observable<any[]> {
        // return of(this.baseColors);
        return super.request('GetColors', {}, 'get');
    }
    getApprovalStatusColors(): Observable<any[]> {
        return super.request('approval-status-colors', {}, 'get');
    }
    saveTheme(settings: AppBrandingSettingView): Observable<any> {
        return super.request('Save', settings, 'post');
    }

    generateRootTheme(selectedThemeColor: any, fontFamily: string = 'eSenseTajawalFlat', fontSize: string = '14px', spaceBase: string = '3px') {

        const brandColors = generateBrandColors(selectedThemeColor);

        const styleTag = document.createElement('style');
        styleTag.setAttribute('data-source', 'brand-colors');
        const colors = brandColors.map((obj) => {
            const [key, value] = Object.entries(obj)[0];
            return `  ${key}: ${value};`;
        }).join('\n');

        styleTag.textContent = `
        :root{
            --font-family: ${fontFamily};
            --font-size-base: ${fontSize};
            --space-base: 3px;
            ${colors}
        }`;

        const existing = document.querySelector('style[data-source="brand-colors"]');
        if (existing) {
            existing.remove();
        }
        document.head.appendChild(styleTag);


    }

    getimgUrl(imgBase64: string): string {
        if (!imgBase64) return '';

        if (imgBase64.startsWith('data:image')) {
            return imgBase64;
        }

        const base64Pattern = /^[A-Za-z0-9+/=]+$/;

        if (base64Pattern.test(imgBase64.replace(/\s/g, ''))) {
            return `data:image/png;base64,${imgBase64}`;
        }

        return imgBase64;
    }

    resetTheme() : Observable<any> {
        return super.request('Reset', {}, 'post');
    }
}

function hexToRgb(hex: string) {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
        hex = hex.split("").map(c => c + c).join("");
    }
    const bigint = parseInt(hex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255
    };
}
function rgbToHex(r: number, g: any, b: any) {
    return "#" + [r, g, b]
        .map(x => {
            const hex = x?.toString(16);
            return hex?.length === 1 ? "0" + hex : hex;
        })
        .join("")
        .toUpperCase();
}

function addAlpha(hex: string, alpha: number) {
    const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, "0").toUpperCase();
    return hex + alphaHex;
}

function lighten({ r, g, b }: { r: number; g: number; b: number; }, percent: number) {
    return {
        r: Math.min(255, Math.round(r + (255 - r) * percent)),
        g: Math.min(255, Math.round(g + (255 - g) * percent)),
        b: Math.min(255, Math.round(b + (255 - b) * percent)),
    };
}

function darken({ r, g, b }: { r: number; g: number; b: number; }, percent: number) {
    return {
        r: Math.max(0, Math.round(r * (1 - percent))),
        g: Math.max(0, Math.round(g * (1 - percent))),
        b: Math.max(0, Math.round(b * (1 - percent))),
    };
}
function generateBrandColors(baseHex: string) {
    const baseRgb = hexToRgb(baseHex);

    const colors = [
        { "--color-brand-900": rgbToHex(...Object.values(darken(baseRgb, 0.55)) as [number, number, number]) },
        { "--color-brand-800": rgbToHex(...Object.values(darken(baseRgb, 0.45)) as [number, number, number]) },
        { "--color-brand-700": rgbToHex(...Object.values(darken(baseRgb, 0.35)) as [number, number, number]) },
        { "--color-brand-612": addAlpha(baseHex.toUpperCase(), 0.12) },
        { "--color-brand-608": addAlpha(baseHex.toUpperCase(), 0.08) },
        { "--color-brand-602": addAlpha(baseHex.toUpperCase(), 0.01) },
        { "--color-brand-600": baseHex.toUpperCase() },
        { "--color-brand-500": rgbToHex(...Object.values(lighten(baseRgb, 0.1)) as [number, number, number]) },
        { "--color-brand-400": rgbToHex(...Object.values(lighten(baseRgb, 0.2)) as [number, number, number]) },
        { "--color-brand-300": rgbToHex(...Object.values(lighten(baseRgb, 0.4)) as [number, number, number]) },
        { "--color-brand-200": rgbToHex(...Object.values(lighten(baseRgb, 0.6)) as [number, number, number]) },
        { "--color-brand-100": rgbToHex(...Object.values(lighten(baseRgb, 0.80)) as [number, number, number]) },
        { "--color-brand-50": rgbToHex(...Object.values(lighten(baseRgb, 0.85)) as [number, number, number]) },
        { "--color-brand-10": rgbToHex(...Object.values(lighten(baseRgb, 0.95)) as [number, number, number]) },
    ];

    return colors;
}
